import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AddCircle } from 'iconsax-react';
import InfoText from '../../../atoms/InfoText';
import Button from '../../../atoms/Button';
import CustomModal from '../../../atoms/CustomModal';
import DeleteModal from '../../../atoms/DeleteModal';
import PreviewProfileItem from '../../PreviewProfileItem';
import EmptyState from '../../EmptyState';
import LanguageForm from './LanguageForm';
import { useDeleteLanguageMutation } from '../../../../store/services/sellerProfileCreationService';

export type UserLanguage = {
  id: string;
  language: { id: number | string; code: string; name: string };
  proficiency: { id: number | string; code: string; name: string };
};

export default function Languages({
  getLangs,
  langs,
}: {
  getLangs: () => void;
  langs: UserLanguage[];
}) {
  const { t } = useTranslation();
  
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | number | null>(null);
  const [modalData, setModalData] = useState<UserLanguage | null>(null);

  const [deleteLanguage] = useDeleteLanguageMutation();

  const handleEdit = (item: any) => {
    setModalData(item);
    setModalOpen(true);
  };

  const handleAdd = () => {
    setModalData(null);
    setModalOpen(true);
  };

  const handleDelete = async () => {
    if (deleteId) {
      await deleteLanguage({ deleteId: String(deleteId) });
      getLangs();
    }
    setDeleteModalOpen(false);
  };

  return (
    <>
      <div className="flex justify-between items-center mb-6" data-testid="profile-creation-languages-header">
        <InfoText
          text={t('components.set_up_your_language')}
          size="2xl"
          className="!font-semibold leading-8 !text-light-button-primary dark:!text-dark-text-primary"
        />
        <button
          onClick={handleAdd}
          type="button"
          data-testid="profile-creation-languages-add-button"
          className="flex items-center justify-center text-sm font-bold bg-none text-[#47A167] gap-2 transition-opacity hover:opacity-80"
        >
          <AddCircle size="20" color="#47A167" variant="Bold" />
          <span>{t('components.add_language')}</span>
        </button>
      </div>

      <CustomModal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        title={t('components.set_up_languages')}
        description={t('components.setupLanguageDescription')}
        width={850}
        centered
        withoutLogo={false}
        variant="supplier"
      >
        {modalOpen && (
          <LanguageForm
            initData={
              modalData
                ? {
                    id: modalData.id,
                    language: String(modalData.language?.id),
                    proficiency: String(modalData.proficiency?.id),
                  }
                : undefined
            }
            onSuccess={() => {
              setModalOpen(false);
              getLangs();
            }}
          />
        )}
      </CustomModal>

      <div className="flex flex-col gap-4" data-testid="profile-creation-languages-list">
        {langs?.length > 0 ? (
          langs.map((item: UserLanguage) => (
            <PreviewProfileItem
              key={item.id}
              isDelete
              isEditable
              title={item.language?.name || ''}
              description={`- ${item.proficiency?.name || ''}`}
              onDelete={() => {
                setDeleteId(item.id);
                setDeleteModalOpen(true);
              }}
              itemId={String(item.id)}
              onEdit={() => handleEdit(item)}
            />
          ))
        ) : (
          <EmptyState onAdd={handleAdd} hideButton borderless />
        )}
      </div>

      <DeleteModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onDelete={handleDelete}
        title={t('components.deleteLanguageTitle')}
        description={t('components.deleteLanguageDescription')}
      />
    </>
  );
}
